import { useMemo, useState } from 'react';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import {
  DistributorRegisterFormData,
  DistributorRegisterSchema,
} from '@/types/auth/RegisterFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useCreateUser, useGetNetworks } from '@/api/user.api';
import { useToast } from '@/hooks/useToast';
import { UserType } from '@/types/auth/UserType';
import { PATHS } from '@/constants/Paths';
import StyledButton from '../styled/StyledButton';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import StyledDropZone from '../styled/StyledDropZone';
import StyledSelect from '../styled/StyledSelect';
import { COUNTRIES } from '../../../../shared/CountriesData';
import SendIcon from '@/assets/icons/send-01.svg?react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { useTranslation } from '@/i18n';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';
import { DistributorKind } from '@/types/auth/DistributorKind';
import { Label } from '../ui/label';

const DEFAULT_NETWORKS = [
  'Agence indépendante',
  'Carrefour Voyages',
  'Cediv',
  'Havas Voyages',
  'Leclerc Voyages',
  'Manor',
  'Prêt à partir',
  'Selectour',
  'TourCom',
  'TUI France',
];

const DistributorRegisterForm = () => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<DistributorRegisterFormData>({
    resolver: zodResolver(DistributorRegisterSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      password: undefined,
      distributorKind: DistributorKind.TravelAgency,
    },
  });

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const firstName = useWatch({ control, name: 'firstName' });
  const lastName = useWatch({ control, name: 'lastName' });
  const email = useWatch({ control, name: 'email' });
  const password = useWatch({ control, name: 'password' });
  const distributorKind = useWatch({
    control,
    name: 'distributorKind',
    defaultValue: DistributorKind.TravelAgency,
  });
  const isTourOperator = distributorKind === DistributorKind.TourOperator;

  const [isFullFormModalOpen, setIsFullFormModalOpen] = useState(false);

  const { data: networks } = useGetNetworks((networks) =>
    networks.map((network) => ({ label: network.name, value: network.id })),
  );

  const networksOptions = useMemo(() => {
    if (networks) {
      return DEFAULT_NETWORKS.map((network) => ({
        label: network,
        value: networks.map((n) => n.label).includes(network)
          ? networks.find((n) => n.label === network)?.value
          : undefined,
      }));
    }
    return [];
  }, [networks]);

  const { mutateAsync: createUser } = useCreateUser();
  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<DistributorRegisterFormData> = async (
    data,
  ) => {
    setErrorMessage(undefined);
    const formData = new FormData();
    const addIf = (key: string, value: string | undefined) => {
      if (value !== undefined) formData.append(key, value);
    };
    formData.append('userType', UserType.Distributor);
    formData.append('companyName', data.agency.name);
    if (data.agency.logo) {
      formData.append('file', data.agency.logo);
    }
    formData.append('address', data.agency.address);
    formData.append('city', data.agency.city);
    formData.append('postalCode', data.agency.postalCode.toString());
    addIf('country', data.agency.country?.value);
    addIf('website', data.agency.website);
    const parsedMarginRate = MarginRateSchema.safeParse(data.agency.marginRate);
    if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
      addIf('marginRate', String(parsedMarginRate.data));
    addIf('siren', data.agency.siren);
    formData.append('firstname', data.firstName);
    formData.append('lastname', data.lastName);
    formData.append('email', data.email);
    formData.append('password', data.password);
    formData.append('phoneNumber', data.phoneNumber);
    formData.append('distributorKind', data.distributorKind);
    const network = data.network;
    if (
      data.distributorKind === DistributorKind.TravelAgency &&
      network?.label
    ) {
      if (network.value) {
        const correspondingNetwork = networks?.find(
          (n) => n.value === network.value,
        );
        if (correspondingNetwork) formData.append('networkId', network.value);
        else formData.append('networkName', network.label);
      } else {
        formData.append('networkName', network.label);
      }
    }

    const response = await toastAsync(createUser(formData), {
      success: t('auth.distributorRegister.accountCreatedSuccess'),
    });

    if (response.status >= 400) {
      setErrorMessage(t('auth.distributorRegister.accountCreationError'));
      return;
    }

    navigate(PATHS.auth.login);
  };

  return (
    <>
      <form
        className="flex w-full flex-col gap-6"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex flex-col w-full gap-5">
          <StyledInput
            control={control}
            title={t('auth.distributorRegister.lastName')}
            name="lastName"
            type="text"
            placeholder={t('auth.distributorRegister.enterLastName')}
          />
          <StyledInput
            control={control}
            title={t('auth.distributorRegister.firstName')}
            name="firstName"
            type="text"
            placeholder={t('auth.distributorRegister.enterFirstName')}
          />
          <StyledInput
            control={control}
            title={t('auth.distributorRegister.email')}
            name="email"
            type="email"
            placeholder={t('auth.distributorRegister.enterEmail')}
          />
          <StyledInput
            control={control}
            title={t('auth.distributorRegister.password')}
            name="password"
            type="password"
          />
        </div>

        {errorMessage && (
          <p className="text-xs text-error-500">{errorMessage}</p>
        )}

        <div className="flex w-full flex-col gap-2">
          <StyledButton
            title={t('auth.distributorRegister.openMyAccount')}
            className="px-4 py-2.5 gap-1.5 rounded-lg"
            disabled={!firstName || !lastName || !email || !password}
            onClick={() => {
              setIsFullFormModalOpen(true);
            }}
          />
        </div>
      </form>
      {isFullFormModalOpen && (
        <dialog
          open
          className="fixed top-0 left-auto w-2/3 bg-brand-25 max-h-full overflow-auto"
        >
          <StyledContainer>
            <StyledFormHeaderWithBreadcrumb
              title={t('auth.distributorRegister.welcome', {
                firstName: firstName ?? '',
              })}
              breadcrumb={
                <StyledButton
                  variant="link"
                  title={t('auth.distributorRegister.backToHome')}
                  className="text-sm text-[#3D3D3D] pl-0"
                  onClick={() => setIsFullFormModalOpen(false)}
                  icon={<ArrowLeftIcon />}
                />
              }
              subTitle={t('auth.distributorRegister.completeRequiredFields')}
            ></StyledFormHeaderWithBreadcrumb>
            <HorizontalLine />

            <StyledFormSection
              sectionTitle={t('auth.distributorRegister.administrativeInfo')}
              shouldTakeHalfWidth
            >
              <div className="flex items-center gap-4 mb-12 mt-0.5">
                <Label className="text-sm font-medium text-black">
                  {t('auth.distributorRegister.youAre')}
                </Label>
                <Controller
                  control={control}
                  name="distributorKind"
                  render={({ field }) => (
                    <div className="flex gap-12">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={field.value === DistributorKind.TravelAgency}
                          onChange={() =>
                            field.onChange(DistributorKind.TravelAgency)
                          }
                          className={`size-4 ${field.value === DistributorKind.TravelAgency ? 'accent-brand-600' : ''}`}
                        />

                        <span className="text-sm">
                          {t('auth.distributorRegister.travelAgency')}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={field.value === DistributorKind.TourOperator}
                          onChange={() =>
                            field.onChange(DistributorKind.TourOperator)
                          }
                          className={`size-4 ${field.value === DistributorKind.TourOperator ? 'accent-brand-600' : ''}`}
                        />
                        <span className="text-sm">
                          {t('auth.distributorRegister.tourOperator')}
                        </span>
                      </label>
                    </div>
                  )}
                />
              </div>
              <StyledInput
                control={control}
                title={t('auth.distributorRegister.name')}
                name="agency.name"
                placeholder={t(
                  'auth.distributorRegister.agencyNamePlaceholder',
                )}
              />
              <StyledDropZone
                control={control}
                name="agency.logo"
                title={t('auth.distributorRegister.logo')}
                placeholder={t('auth.distributorRegister.logoPlaceholder')}
              />
              <StyledInput
                control={control}
                title={t('auth.distributorRegister.address')}
                name="agency.address"
                placeholder={t('auth.distributorRegister.addAddress')}
              />
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.city')}
                  name="agency.city"
                  placeholder={t('auth.distributorRegister.addCity')}
                />
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.postalCode')}
                  name="agency.postalCode"
                  type="number"
                  hideNumberFieldArrows
                  placeholder={t('auth.distributorRegister.addPostalCode')}
                />
              </div>
              <StyledSelect
                options={COUNTRIES}
                control={control}
                title={t('auth.distributorRegister.country')}
                name="agency.country"
                placeholder={t('auth.distributorRegister.selectCountry')}
              />
              <StyledInput
                control={control}
                title={t('auth.distributorRegister.siren')}
                name="agency.siren"
                placeholder={t('auth.distributorRegister.addCode')}
              />
              <StyledInput
                control={control}
                title={t('auth.distributorRegister.website')}
                name="agency.website"
                placeholder={t('auth.distributorRegister.websitePlaceholder')}
              />
              <MarginRateStyledInput
                control={control}
                title={t('auth.distributorRegister.marginRate')}
                name="agency.marginRate"
                placeholder={t('auth.distributorRegister.addRate')}
              />
            </StyledFormSection>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('auth.distributorRegister.mainContact')}
              shouldTakeHalfWidth
            >
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.lastName') + ' *'}
                  name="lastName"
                />
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.firstName') + ' *'}
                  name="firstName"
                />
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.email') + ' *'}
                  name="email"
                  type="email"
                />
                <StyledInput
                  control={control}
                  title={t('auth.distributorRegister.phone')}
                  name="phoneNumber"
                  type="tel"
                  placeholder={t('auth.distributorRegister.addPhoneNumber')}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-1/2">
                  <StyledInput
                    control={control}
                    title={t('auth.distributorRegister.password') + ' *'}
                    name="password"
                    type="password"
                  />
                </div>
              </div>
            </StyledFormSection>
            {!isTourOperator && (
              <>
                <HorizontalLine />
                <StyledFormSection
                  sectionTitle={t('auth.distributorRegister.network')}
                  shouldTakeHalfWidth
                >
                  <StyledSelect
                    options={networksOptions}
                    control={control}
                    name="network"
                    title={t('auth.distributorRegister.networkTitle')}
                    placeholder={t('auth.distributorRegister.selectNetwork')}
                    isClearable
                  />
                </StyledFormSection>
              </>
            )}
            <div className="flex flex-row-reverse p-6">
              <StyledButton
                title={t('auth.distributorRegister.sendRequest')}
                onClick={handleSubmit(submitForm)}
                disabled={!isValid}
                icon={<SendIcon className="size-5" />}
              />
            </div>
          </StyledContainer>
        </dialog>
      )}
    </>
  );
};

export default DistributorRegisterForm;
